import { css } from "@emotion/react";
import { FC } from "react";
import { useSelector } from "react-redux";
import { RouteComponentProps } from "react-router";

import { Alert, Button, SpacingToken } from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { StyledLink } from "components/links/StyledLink";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { isInIframe } from "helpers/app";
import { useIframeNavigateTo } from "hooks/use-iframe-navigate-to";
import { AutomationsRoutes } from "routes/automations/constants";
import { AutomationsEmptyState } from "routes/automations/shared/components/AutomationsEmptyState";
import { AutomationsSectionHeader } from "routes/automations/shared/components/AutomationsSectionHeader";
import { AutomationsTemplatesSection } from "routes/automations/shared/components/AutomationsTemplatesSection";
import { selectIsAdmin } from "store/entities/agents";
import { selectRules } from "store/entities/rules/selectors";
import { withVar } from "themes/helpers";

import { PremadeRulesCarousel } from "./PremadeRulesCarousel";
import { RuleCollection } from "./RuleCollection";

const PermissionsAlert: FC = () => {
  const embeddedInIframe = isInIframe();
  const { navigateTo } = useIframeNavigateTo();

  return (
    <Alert kind="info" data-testid="alert-permissions">
      You&apos;re not allowed to manage rules.{" "}
      {embeddedInIframe ? (
        <Button kind="link" css={css({ fontWeight: 400 })} onClick={() => navigateTo("/admin/teammates")}>
          Contact one of your Admins
        </Button>
      ) : (
        <StyledLink kind="link-light" to="/agents">
          Contact one of your Admins
        </StyledLink>
      )}{" "}
      to do so.
    </Alert>
  );
};

interface IProps {
  history: RouteComponentProps["history"];
}
const EmptyStateView: FC<IProps> = ({ history }) => {
  const isAdmin = useSelector(selectIsAdmin);

  const handleCreateRuleClick = (): void => {
    trackEngagement("Workflows: Add workflow clicked", undefined, { place: "empty-state" });
    history.push(`${AutomationsRoutes.AddRule}`);
  };

  return (
    <Flex column gap={withVar(SpacingToken.Spacing8)}>
      {!isAdmin && <PermissionsAlert />}
      <AutomationsEmptyState
        title="No rules yet"
        description="Create your first rule to automate repetitive and routine tasks."
        primaryAction={{
          label: "Create rule",
          disabled: !isAdmin,
          onClick: handleCreateRuleClick,
          testId: "add-btn-emptystate",
        }}
        templates={<PremadeRulesCarousel />}
      />
    </Flex>
  );
};

export const MainViewContent: FC<IProps> = ({ history }) => {
  const rules = useSelector(selectRules);
  const isAdmin = useSelector(selectIsAdmin);
  const embeddedInIframe = isInIframe();

  if (!rules.length) {
    return <EmptyStateView history={history} />;
  }

  return (
    <>
      {!isAdmin && <PermissionsAlert />}
      {!embeddedInIframe && (
        <Block>
          <Button
            kind="primary"
            disabled={!isAdmin}
            onClick={() => {
              trackEngagement("Workflows: Add workflow clicked", EventPlace.Automations, { place: "main" });
              history.push(AutomationsRoutes.AddRule);
            }}
            data-testid="add-btn-main"
          >
            Create rule
          </Button>
        </Block>
      )}
      <AutomationsTemplatesSection
        description="Select from agents' favorite rule templates and adjust them to your case."
        templates={<PremadeRulesCarousel />}
      />
      <Flex column gap={withVar(SpacingToken.Spacing4)}>
        <AutomationsSectionHeader title="Created rules" />
        <RuleCollection rules={rules} />
      </Flex>
    </>
  );
};
